import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, Hash, Share2, ChevronRight, BookOpen, Twitter, Linkedin, Link as LinkIcon, Pin, ArrowUpRight, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

// -- Mock data ---------------------------------------------------------------
const MOCK_ARTICLES = [
  {
    id: '1',
    slug: 'why-every-indian-adult-needs-a-will',
    title: 'Why Every Indian Adult Needs a Will — Regardless of Age',
    excerpt: 'A will is not just for the elderly. Here is why every adult above 18 should have one, and the surprising risks of dying intestate.',
    cover: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=70',
    hashtags: ['wills', 'estate', 'legal'],
    readTime: 7,
    date: '2026-05-19',
    pinned: true,
    author: 'WillKart Editorial',
  },
  {
    id: '2',
    slug: 'demystifying-revocable-vs-irrevocable-trusts',
    title: 'Revocable vs Irrevocable Trusts — Demystified',
    excerpt: 'Choosing between revocable and irrevocable trusts can shape your legacy for decades. We break down the differences in plain language.',
    cover: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=70',
    hashtags: ['trusts', 'estate', 'tax'],
    readTime: 11,
    date: '2026-05-12',
    pinned: true,
    author: 'WillKart Editorial',
  },
  {
    id: '3',
    slug: 'life-vault-what-to-store',
    title: 'Your Life Vault — What You Should Store and Why',
    excerpt: 'A practical guide to the 12 documents your family will need on day one. Spoiler: it is not just bank statements.',
    cover: 'https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=1200&q=70',
    hashtags: ['vault', 'guides'],
    readTime: 8,
    date: '2026-05-05',
    pinned: false,
    author: 'WillKart Editorial',
  },
  {
    id: '4',
    slug: 'digital-assets-after-death',
    title: 'What Happens to Your Email, Crypto, and Cloud Drives When You Die?',
    excerpt: 'Digital assets fall through the cracks of traditional estate planning. Here is how to make sure they do not vanish.',
    cover: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1200&q=70',
    hashtags: ['vault', 'digital', 'guides'],
    readTime: 9,
    date: '2026-04-28',
    pinned: false,
    author: 'WillKart Editorial',
  },
  {
    id: '5',
    slug: 'tax-implications-of-inheritance',
    title: 'Tax Implications of Inheritance in India (2026 Update)',
    excerpt: 'No inheritance tax — but capital gains, gift tax, and TDS still apply. The 2026 rules every beneficiary should know.',
    cover: null,
    hashtags: ['tax', 'legal', 'estate'],
    readTime: 6,
    date: '2026-04-20',
    pinned: false,
    author: 'WillKart Editorial',
  },
  {
    id: '6',
    slug: 'choosing-your-executor',
    title: 'Choosing the Right Executor — More Than a Trusted Friend',
    excerpt: 'Your executor is the conductor of your final wishes. We outline 5 traits that matter more than personal closeness.',
    cover: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=1200&q=70',
    hashtags: ['wills', 'guides'],
    readTime: 5,
    date: '2026-04-15',
    pinned: false,
    author: 'WillKart Editorial',
  },
  {
    id: '7',
    slug: 'how-to-update-your-will',
    title: 'When (and How) to Update Your Will',
    excerpt: 'Marriage, divorce, a new home, a new child — each is a trigger event. A 90-second checklist to keep your will current.',
    cover: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&q=70',
    hashtags: ['wills', 'guides'],
    readTime: 4,
    date: '2026-04-08',
    pinned: false,
    author: 'WillKart Editorial',
  },
  {
    id: '8',
    slug: 'guardianship-for-minor-children',
    title: 'Guardianship for Minor Children — The Conversation No One Wants to Have',
    excerpt: 'Naming a guardian is one of the most consequential decisions in a will. Here is how to approach it thoughtfully.',
    cover: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=1200&q=70',
    hashtags: ['wills', 'guides', 'family'],
    readTime: 10,
    date: '2026-04-01',
    pinned: false,
    author: 'WillKart Editorial',
  },
];

const ALL_HASHTAGS = Array.from(new Set(MOCK_ARTICLES.flatMap(a => a.hashtags))).sort();

// -- Reusable bits -----------------------------------------------------------
const HashChip = ({ tag, active, onClick }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
      active
        ? 'bg-amber-500 text-slate-950 shadow-lg shadow-amber-500/30'
        : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700 border border-slate-700'
    }`}
  >
    <Hash className="w-3 h-3" />{tag}
  </button>
);

const SearchBar = ({ value, onChange, placeholder = 'Search articles...' }) => (
  <div className="relative">
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
    <Input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="pl-10 bg-slate-900/80 border-slate-700 text-slate-100 placeholder:text-slate-500 focus:border-amber-500"
    />
  </div>
);

// -- Design 1: Editor's Desk -----------------------------------------------
const Design1EditorsDesk = ({ articles }) => {
  const [hero, ...rest] = articles;
  return (
    <div className="space-y-12">
      {/* Hero */}
      {hero && (
        <article className="grid lg:grid-cols-2 gap-8 items-center group cursor-pointer">
          <div className="relative aspect-[5/3] rounded-2xl overflow-hidden border border-slate-800">
            <img src={hero.cover || 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200'} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute top-4 left-4 flex gap-2">
              {hero.pinned && (
                <span className="inline-flex items-center gap-1 bg-amber-500 text-slate-950 px-2 py-1 rounded-full text-xs font-bold">
                  <Pin className="w-3 h-3" />FEATURED
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="flex gap-2 mb-4">
              {hero.hashtags.slice(0, 3).map(t => <HashChip key={t} tag={t} />)}
            </div>
            <h1 className="text-4xl lg:text-5xl font-serif text-slate-100 leading-tight mb-4 group-hover:text-amber-400 transition-colors">
              {hero.title}
            </h1>
            <p className="text-lg text-slate-400 mb-6">{hero.excerpt}</p>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <span>{hero.author}</span>
              <span>•</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{hero.readTime} min read</span>
            </div>
          </div>
        </article>
      )}
      {/* Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rest.map(a => (
          <article key={a.id} className="group cursor-pointer">
            <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-4 border border-slate-800 bg-slate-900">
              {a.cover ? (
                <img src={a.cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-700"><BookOpen className="w-8 h-8" /></div>
              )}
            </div>
            <div className="flex gap-2 mb-2">
              {a.hashtags.slice(0, 2).map(t => <HashChip key={t} tag={t} />)}
            </div>
            <h3 className="text-lg font-semibold text-slate-100 mb-2 group-hover:text-amber-400 transition-colors">{a.title}</h3>
            <p className="text-sm text-slate-400 line-clamp-2 mb-3">{a.excerpt}</p>
            <div className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" />{a.readTime} min</div>
          </article>
        ))}
      </div>
    </div>
  );
};

// -- Design 2: Vault Index -------------------------------------------------
const Design2VaultIndex = ({ articles }) => (
  <div className="divide-y divide-slate-800/60 border border-slate-800 rounded-xl overflow-hidden">
    {articles.map(a => (
      <article key={a.id} className="group flex gap-4 p-4 hover:bg-slate-900/50 cursor-pointer transition-colors">
        <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-slate-800 border border-slate-700">
          {a.cover ? (
            <img src={a.cover} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-600"><BookOpen className="w-6 h-6" /></div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 text-xs text-slate-500">
            {a.pinned && <span className="inline-flex items-center gap-1 text-amber-400"><Pin className="w-3 h-3" />Featured</span>}
            <span>{new Date(a.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            <span>•</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{a.readTime} min</span>
          </div>
          <h3 className="text-base font-semibold text-slate-100 mb-1 group-hover:text-amber-400 transition-colors">{a.title}</h3>
          <p className="text-sm text-slate-400 line-clamp-2 mb-2">{a.excerpt}</p>
          <div className="flex gap-1.5 flex-wrap">
            {a.hashtags.map(t => (
              <span key={t} className="text-xs text-slate-500 hover:text-amber-400">#{t}</span>
            ))}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-amber-400 self-center transition-colors" />
      </article>
    ))}
  </div>
);

// -- Design 3: Tile Wall (Asymmetric Mosaic) -------------------------------
const Design3TileWall = ({ articles }) => {
  // Distribute across 3 columns with varied heights
  const cols = [[], [], []];
  articles.forEach((a, i) => cols[i % 3].push(a));
  return (
    <div className="grid md:grid-cols-3 gap-5">
      {cols.map((col, ci) => (
        <div key={ci} className="space-y-5">
          {col.map((a, i) => {
            const tall = a.cover && (i + ci) % 2 === 0;
            return (
              <article key={a.id} className="group cursor-pointer rounded-2xl overflow-hidden border border-slate-800 bg-slate-900/60 hover:border-amber-500/50 transition-all hover:-translate-y-1">
                {a.cover && (
                  <div className={`relative w-full overflow-hidden bg-slate-800 ${tall ? 'aspect-[3/4]' : 'aspect-[4/3]'}`}>
                    <img src={a.cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {a.pinned && (
                      <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-amber-500 text-slate-950 px-2 py-1 rounded-full text-xs font-bold">
                        <Pin className="w-3 h-3" />FEATURED
                      </span>
                    )}
                  </div>
                )}
                <div className="p-4">
                  {!a.cover && a.pinned && (
                    <span className="inline-flex items-center gap-1 bg-amber-500 text-slate-950 px-2 py-1 rounded-full text-xs font-bold mb-3">
                      <Pin className="w-3 h-3" />FEATURED
                    </span>
                  )}
                  <div className="flex gap-1.5 flex-wrap mb-2">
                    {a.hashtags.slice(0, 3).map(t => <HashChip key={t} tag={t} />)}
                  </div>
                  <h3 className={`font-semibold text-slate-100 mb-2 group-hover:text-amber-400 transition-colors ${a.cover ? 'text-base' : 'text-lg'}`}>{a.title}</h3>
                  <p className="text-sm text-slate-400 line-clamp-3 mb-3">{a.excerpt}</p>
                  <div className="text-xs text-slate-500 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{a.readTime} min</span>
                    <span>•</span>
                    <span>{new Date(a.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// -- Design 4: Knowledge Atlas (Docs-style) --------------------------------
const Design4KnowledgeAtlas = ({ articles }) => {
  const [activeHash, setActiveHash] = useState(null);
  const groups = useMemo(() => {
    const map = {};
    articles.forEach(a => a.hashtags.forEach(t => {
      if (!map[t]) map[t] = [];
      map[t].push(a);
    }));
    return map;
  }, [articles]);
  const visible = activeHash ? groups[activeHash] : articles;
  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Left sidebar */}
      <aside className="col-span-3 border-r border-slate-800 pr-6 space-y-1">
        <button onClick={() => setActiveHash(null)} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${!activeHash ? 'bg-amber-500/10 text-amber-400' : 'text-slate-400 hover:bg-slate-800/50'}`}>
          All Articles ({articles.length})
        </button>
        <div className="text-xs text-slate-600 uppercase tracking-wider mt-4 mb-2 px-3">Hashtags</div>
        {Object.keys(groups).map(t => (
          <button key={t} onClick={() => setActiveHash(t)} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition flex justify-between items-center ${activeHash === t ? 'bg-amber-500/10 text-amber-400' : 'text-slate-400 hover:bg-slate-800/50'}`}>
            <span className="flex items-center gap-1"><Hash className="w-3 h-3" />{t}</span>
            <span className="text-xs text-slate-600">{groups[t].length}</span>
          </button>
        ))}
      </aside>
      {/* Main */}
      <main className="col-span-9 space-y-3">
        {visible.map(a => (
          <article key={a.id} className="group flex gap-4 p-4 rounded-lg hover:bg-slate-900/50 cursor-pointer transition border border-transparent hover:border-slate-700">
            <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-slate-800">
              {a.cover ? <img src={a.cover} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-600"><BookOpen className="w-5 h-5" /></div>}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-slate-100 group-hover:text-amber-400 transition-colors">{a.title}</h3>
              <p className="text-sm text-slate-400 line-clamp-1 mt-1">{a.excerpt}</p>
              <div className="flex gap-1.5 mt-2 text-xs text-slate-500">
                {a.hashtags.map(t => <span key={t}>#{t}</span>)}
                <span>•</span>
                <span>{a.readTime} min</span>
              </div>
            </div>
          </article>
        ))}
      </main>
    </div>
  );
};

// -- Design 5: Chronicle (Vertical Timeline) -------------------------------
const Design5Chronicle = ({ articles }) => (
  <div className="relative">
    <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-500/50 via-slate-700 to-transparent" />
    <div className="space-y-12">
      {articles.map((a, i) => (
        <article key={a.id} className={`group relative grid md:grid-cols-2 gap-6 items-center ${i % 2 === 0 ? '' : 'md:flex-row-reverse'}`}>
          {/* Dot on timeline */}
          <div className="absolute left-4 md:left-1/2 w-3 h-3 -translate-x-1/2 rounded-full bg-amber-500 ring-4 ring-slate-950" />
          {/* Date pill */}
          <div className={`absolute left-12 md:left-auto ${i % 2 === 0 ? 'md:right-[calc(50%+2rem)]' : 'md:left-[calc(50%+2rem)]'} top-0 text-xs text-slate-500 flex items-center gap-1`}>
            <Calendar className="w-3 h-3" />{new Date(a.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
          {/* Card */}
          <div className={`${i % 2 === 0 ? 'md:col-start-1' : 'md:col-start-2'} ml-12 md:ml-0 mt-6 cursor-pointer`}>
            <div className="rounded-2xl overflow-hidden border border-slate-800 bg-slate-900/60 hover:border-amber-500/50 transition-all">
              {a.cover && (
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={a.cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-5">
                <div className="flex gap-1.5 mb-2 flex-wrap">
                  {a.hashtags.slice(0, 3).map(t => <HashChip key={t} tag={t} />)}
                </div>
                <h3 className="text-xl font-semibold text-slate-100 mb-2 group-hover:text-amber-400 transition-colors">{a.title}</h3>
                <p className="text-sm text-slate-400 mb-3">{a.excerpt}</p>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{a.readTime} min</span>
                  <span className="flex items-center gap-1 text-amber-400 group-hover:gap-2 transition-all">Read more <ArrowUpRight className="w-3 h-3" /></span>
                </div>
              </div>
            </div>
          </div>
          {/* Spacer on opposite side */}
          <div className="hidden md:block" />
        </article>
      ))}
    </div>
  </div>
);

// -- Main preview page -----------------------------------------------------
const DESIGNS = [
  { id: 1, name: "#1 Editor's Desk", subtitle: 'Magazine hero + grid', Component: Design1EditorsDesk },
  { id: 2, name: '#2 Vault Index', subtitle: 'Compact horizontal rows', Component: Design2VaultIndex },
  { id: 3, name: '#3 Tile Wall', subtitle: 'Asymmetric mosaic', Component: Design3TileWall, recommended: true },
  { id: 4, name: '#4 Knowledge Atlas', subtitle: 'Docs-style sidebar', Component: Design4KnowledgeAtlas },
  { id: 5, name: '#5 Chronicle', subtitle: 'Vertical timeline', Component: Design5Chronicle },
];

const KnowledgePreview = () => {
  const [activeDesign, setActiveDesign] = useState(3);
  const [search, setSearch] = useState('');
  const [activeHash, setActiveHash] = useState(null);

  const filtered = useMemo(() => {
    let list = MOCK_ARTICLES.slice().sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || new Date(b.date) - new Date(a.date));
    if (activeHash) list = list.filter(a => a.hashtags.includes(activeHash));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(a => a.title.toLowerCase().includes(q) || a.excerpt.toLowerCase().includes(q));
    }
    return list;
  }, [search, activeHash]);

  const active = DESIGNS.find(d => d.id === activeDesign);
  const Component = active.Component;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="knowledge-preview-page">
      {/* Top design switcher */}
      <div className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/" className="text-amber-400 text-sm hover:underline">← Back to WillKart</Link>
              <span className="text-slate-700">|</span>
              <h1 className="text-xl font-bold text-slate-100">Knowledge — Design Preview</h1>
            </div>
            <div className="text-xs text-slate-500">Click a design below to switch · Search & hashtag filter are live</div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {DESIGNS.map(d => (
              <button
                key={d.id}
                onClick={() => setActiveDesign(d.id)}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                  activeDesign === d.id
                    ? 'bg-amber-500 text-slate-950 border-amber-500 shadow-lg shadow-amber-500/30'
                    : 'bg-slate-900 text-slate-300 border-slate-800 hover:border-slate-600'
                }`}
                data-testid={`design-tab-${d.id}`}
              >
                <div className="flex items-center gap-2">
                  <span>{d.name}</span>
                  {d.recommended && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${activeDesign === d.id ? 'bg-slate-950 text-amber-500' : 'bg-amber-500/20 text-amber-400'}`}>RECOMMENDED</span>
                  )}
                </div>
                <div className={`text-xs mt-0.5 ${activeDesign === d.id ? 'text-slate-900' : 'text-slate-500'}`}>{d.subtitle}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Page header */}
      <div className="max-w-7xl mx-auto px-6 pt-12 pb-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 text-xs font-medium mb-4">
            <BookOpen className="w-3 h-3" />KNOWLEDGE
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-100 mb-3">{active.name}</h1>
          <p className="text-slate-400">{active.subtitle} · {filtered.length} {filtered.length === 1 ? 'article' : 'articles'}</p>
        </div>

        {/* Filter bar (shared by all designs) */}
        <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center mb-8">
          <SearchBar value={search} onChange={setSearch} placeholder="Search by title, excerpt, or hashtag..." />
          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-xs text-slate-500 mr-1">Filter:</span>
            <HashChip tag="all" active={!activeHash} onClick={() => setActiveHash(null)} />
            {ALL_HASHTAGS.map(t => (
              <HashChip key={t} tag={t} active={activeHash === t} onClick={() => setActiveHash(t === activeHash ? null : t)} />
            ))}
          </div>
        </div>

        {/* Active design body */}
        <div className="mb-12">
          <Component articles={filtered} />
        </div>

        {/* Share buttons preview */}
        <div className="mt-16 border-t border-slate-800 pt-8">
          <div className="text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Article reader will include share buttons:</p>
            <div className="inline-flex gap-2">
              <Button variant="outline" size="sm" className="border-slate-700 text-slate-300"><Twitter className="w-4 h-4 mr-1" />Twitter</Button>
              <Button variant="outline" size="sm" className="border-slate-700 text-slate-300"><Linkedin className="w-4 h-4 mr-1" />LinkedIn</Button>
              <Button variant="outline" size="sm" className="border-slate-700 text-slate-300"><LinkIcon className="w-4 h-4 mr-1" />Copy link</Button>
              <Button variant="outline" size="sm" className="border-slate-700 text-slate-300"><Share2 className="w-4 h-4 mr-1" />Native share</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgePreview;
